import { X, ChevronDown, Globe, BarChart3, GraduationCap } from 'lucide-react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { SetupScoreCard } from '../settings/SetupScoreCard';
import { useLanguage } from '../../../contexts/LanguageContext';
import { supportedLanguages, SupportedLanguage } from '../../../i18n';
import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useReadOnly } from '../../../hooks/useReadOnly';
import { useOnboardingPreference } from '../../../hooks/useOnboardingPreference';
import { TabHeader } from '../../common/TabHeader';

// V4-FRONT-SETTINGS-BRAND-LOCK-CONDITION 2026-06-03 — les 3 sections "brand" lockées
// (Typographie/Palette/Logo) concernaient le THÈME d'affichage de l'interface Raiso (≠ charte
// graphique IA, qui se gère dans Positionnement > Charte). Jamais déverrouillées, elles ont été
// supprimées avec tout le dead code thème associé (ColorPicker, useColors, useTypography, upload
// logo). Les contextes ColorContext/TypographyContext qui APPLIQUENT le thème app-wide restent intacts.

interface SettingsPanelProps {
  onClose: () => void;
}

export function SettingsTab({ onClose }: SettingsPanelProps) {
  const { language, setLanguage, t } = useLanguage();
  const readOnly = useReadOnly();
  const { currentSpace } = useAuth();
  // V4-FRONT-WIZARD-POST-GEN-HIDE 2026-05-29 — Toggle pour réafficher le wizard
  // d'onboarding une fois qu'il a été masqué (per-space, localStorage-backed).
  const { visible: onboardingVisible, setVisible: setOnboardingVisible } =
    useOnboardingPreference(currentSpace?.id);

  // États pour les sections extensibles
  const [expandedSection, setExpandedSection] = useState<string | null>('language');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header — coque partagée V4-FRONT-HEADERS-UNIFORM */}
      <TabHeader title={t.settings.title} onClose={onClose} />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          {/* Setup Score */}
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid var(--color-border)` }}>
            <button
              onClick={() => toggleSection('setup-score')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{
                backgroundColor: expandedSection === 'setup-score' ? 'var(--color-surface)' : 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = expandedSection === 'setup-score' ? 'var(--color-surface)' : 'transparent'}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-text-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>
                  {t.settings.setupScore || 'Score de configuration'}
                </Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${
                  expandedSection === 'setup-score' ? 'rotate-180' : ''
                }`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {expandedSection === 'setup-score' && (
              <div className="p-4" style={{ borderTop: `1px solid var(--color-border)`, backgroundColor: 'var(--color-surface)' }}>
                <SetupScoreCard />
              </div>
            )}
          </div>

          {/* Language settings */}
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid var(--color-border)` }}>
            <button
              onClick={() => toggleSection('language')}
              className="w-full flex items-center justify-between p-4 transition-colors"
              style={{
                backgroundColor: expandedSection === 'language' ? 'var(--color-surface)' : 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = expandedSection === 'language' ? 'var(--color-surface)' : 'transparent'}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <Globe className="w-5 h-5" style={{ color: 'var(--color-text-primary)' }} />
                </div>
                <Label className="text-sm cursor-pointer" style={{ color: 'var(--color-text-primary)' }}>{t.settings.language}</Label>
              </div>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${
                  expandedSection === 'language' ? 'rotate-180' : ''
                }`}
                style={{ color: 'var(--color-text-secondary)' }}
              />
            </button>
            {expandedSection === 'language' && (
              <div className="p-4" style={{ borderTop: `1px solid var(--color-border)`, backgroundColor: 'var(--color-surface)' }}>
                <div className="flex flex-wrap gap-2">
                  {supportedLanguages.map((lang) => {
                    const isSelected = language === lang.code;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => !readOnly && setLanguage(lang.code as SupportedLanguage)}
                        disabled={readOnly}
                        className="px-4 py-2 rounded-full text-sm transition-all"
                        style={{
                          backgroundColor: isSelected ? 'var(--color-primary)' : 'transparent',
                          border: isSelected ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                          color: isSelected ? 'var(--color-text-light)' : 'var(--color-text-primary)',
                        }}
                      >
                        {lang.flag} {lang.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* V4-FRONT-WIZARD-POST-GEN-HIDE 2026-05-29 — Toggle pour réafficher
              le tutoriel d'onboarding (wizard 4 étapes) si l'utilisateur l'a masqué. */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: `1px solid var(--color-border)` }}
            data-testid="settings-onboarding"
          >
            <div className="w-full flex items-center justify-between p-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--color-surface)' }}
                >
                  <GraduationCap className="w-5 h-5" style={{ color: 'var(--color-text-primary)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    {t.settings?.onboardingToggleLabel ?? 'Afficher le tutoriel d\'onboarding'}
                  </Label>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                    {t.settings?.onboardingToggleDescription ?? 'Réafficher le guide des 4 étapes (Positionnement, Veille, Sources, Canaux) au-dessus de la galerie de posts.'}
                  </p>
                </div>
              </div>
              <Switch
                checked={onboardingVisible}
                onCheckedChange={setOnboardingVisible}
                disabled={readOnly}
                aria-label={t.settings?.onboardingToggleLabel ?? 'Afficher le tutoriel d\'onboarding'}
                data-testid="settings-onboarding-toggle"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
